import React, { useState, useRef, forwardRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { X, ExternalLink, ChevronLeft, ChevronRight, StickyNote, Image as ImageIcon, Globe, Youtube } from 'lucide-react'

// Utility function for class names
function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

interface Project {
  id: string
  image: string
  title: string
  type?: string
}

interface AnimatedFolderProps {
  title: string
  projects: Project[]
  color?: string
  className?: string
  onClick?: () => void
  itemCount?: number  // Optional override for display count
}

// Color schemes for folders - lighter, subtle colors
const folderColorSchemes = {
  blue: {
    front: 'linear-gradient(180deg, #93C5FD 0%, #60A5FA 100%)',
    back: 'linear-gradient(180deg, #BFDBFE 0%, #93C5FD 100%)',
    tab: '#DBEAFE',
  },
  purple: {
    front: 'linear-gradient(180deg, #99F6E4 0%, #5EEAD4 100%)',
    back: 'linear-gradient(180deg, #CCFBF1 0%, #99F6E4 100%)',
    tab: '#F0FDFA',
  },
  green: {
    front: 'linear-gradient(180deg, #99F6E4 0%, #5EEAD4 100%)',
    back: 'linear-gradient(180deg, #CCFBF1 0%, #99F6E4 100%)',
    tab: '#F0FDFA',
  },
  orange: {
    front: 'linear-gradient(180deg, #FCD34D 0%, #FBBF24 100%)',
    back: 'linear-gradient(180deg, #FEF3C7 0%, #FCD34D 100%)',
    tab: '#FEF9C3',
  },
  red: {
    front: 'linear-gradient(180deg, #FCA5A5 0%, #F87171 100%)',
    back: 'linear-gradient(180deg, #FECACA 0%, #FCA5A5 100%)',
    tab: '#FEE2E2',
  },
  pink: {
    front: 'linear-gradient(180deg, #F9A8D4 0%, #F472B6 100%)',
    back: 'linear-gradient(180deg, #FBCFE8 0%, #F9A8D4 100%)',
    tab: '#FCE7F3',
  },
  yellow: {
    front: 'linear-gradient(180deg, #FDE047 0%, #FACC15 100%)',
    back: 'linear-gradient(180deg, #FEF9C3 0%, #FDE047 100%)',
    tab: '#FEFCE8',
  },
}

// Get type icon for fallback - Fabric-style icons
const getTypeIcon = (type?: string) => {
  switch (type) {
    case 'note': return <StickyNote className="w-5 h-5 text-amber-600" />
    case 'screenshot': return <ImageIcon className="w-5 h-5 text-gray-500" />
    case 'youtube': return <Youtube className="w-5 h-5 text-red-500" />
    default: return <img src="/worldwide.png" alt="Web" className="w-5 h-5 object-contain" />
  }
}

// Fabric-style solid background colors for cards without images
const getCardBackground = (type?: string) => {
  switch (type) {
    case 'note': return '#FEF9E7' // Warm cream/yellow for notes
    case 'screenshot': return '#F8FAFC' // Light gray for screenshots  
    case 'youtube': return '#FEF2F2' // Light red tint
    default: return '#F0F9FF' // Light blue tint for web content
  }
}

export default function AnimatedFolder({ 
  title, 
  projects, 
  color = 'blue',
  className,
  onClick,
  itemCount 
}: AnimatedFolderProps) {
  const displayCount = itemCount ?? projects.length
  const [isHovered, setIsHovered] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [sourceRect, setSourceRect] = useState<DOMRect | null>(null)
  const [hiddenCardId, setHiddenCardId] = useState<string | null>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  const colors = folderColorSchemes[color as keyof typeof folderColorSchemes] || folderColorSchemes.blue

  const handleProjectClick = (project: Project, index: number) => {
    const cardEl = cardRefs.current[index]
    if (cardEl) {
      setSourceRect(cardEl.getBoundingClientRect())
    }
    setSelectedIndex(index)
    setHiddenCardId(project.id)
  }

  const handleCloseLightbox = () => {
    setSelectedIndex(null)
    setSourceRect(null)
  }

  const handleCloseComplete = () => {
    setHiddenCardId(null)
  }

  const handleNavigate = (newIndex: number) => {
    setSelectedIndex(newIndex)
    setHiddenCardId(projects[newIndex]?.id || null)
  }

  const handleFolderClick = () => {
    if (onClick) {
      onClick()
    }
  }

  return (
    <>
      <div
        className={cn(
          "relative flex flex-col items-center justify-center",
          "p-6 rounded-2xl cursor-pointer",
          "bg-white dark:bg-gray-800",
          "transition-all duration-500 ease-out",
          "hover:shadow-xl",
          "group",
          className,
        )}
        style={{
          minWidth: "160px",
          minHeight: "210px",
          perspective: "1000px",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleFolderClick}
      >
        {/* Subtle background glow on hover - removed */}

        <div className="relative flex items-center justify-center mb-2" style={{ height: "110px", width: "130px" }}>
          {/* Folder back layer - z-index 10 */}
          <div
            className="absolute w-24 h-16 rounded-lg shadow-md"
            style={{
              background: colors.back,
              transformOrigin: "bottom center",
              transform: isHovered ? "rotateX(-15deg)" : "rotateX(0deg)",
              transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 10,
            }}
          />

          {/* Folder tab - z-index 10 */}
          <div
            className="absolute w-8 h-3 rounded-t-md"
            style={{
              background: colors.tab,
              top: "calc(50% - 32px - 10px)",
              left: "calc(50% - 48px + 10px)",
              transformOrigin: "bottom center",
              transform: isHovered ? "rotateX(-25deg) translateY(-2px)" : "rotateX(0deg)",
              transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 10,
            }}
          />

          {/* Project cards - z-index 20, between back and front */}
          <div
            className="absolute"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 20,
            }}
          >
            {projects.slice(0, 3).map((project, index) => (
              <ProjectCard
                key={project.id}
                ref={(el) => {
                  cardRefs.current[index] = el
                }}
                image={project.image}
                title={project.title}
                type={project.type}
                delay={index * 80}
                isVisible={isHovered}
                index={index}
                onClick={() => handleProjectClick(project, index)}
                isSelected={hiddenCardId === project.id}
              />
            ))}
          </div>

          {/* Folder front layer - z-index 30 */}
          <div
            className="absolute w-24 h-16 rounded-lg shadow-lg"
            style={{
              background: colors.front,
              top: "calc(50% - 32px + 4px)",
              transformOrigin: "bottom center",
              transform: isHovered ? "rotateX(25deg) translateY(8px)" : "rotateX(0deg)",
              transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 30,
            }}
          />

          {/* Folder shine effect - z-index 31 */}
          <div
            className="absolute w-24 h-16 rounded-lg overflow-hidden pointer-events-none"
            style={{
              top: "calc(50% - 32px + 4px)",
              background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)",
              transformOrigin: "bottom center",
              transform: isHovered ? "rotateX(25deg) translateY(8px)" : "rotateX(0deg)",
              transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 31,
            }}
          />
        </div>

        {/* Folder title */}
        <h3
          className="text-base font-semibold text-gray-900 dark:text-white mt-3 text-center transition-all duration-300 px-2 truncate max-w-full"
          style={{
            transform: isHovered ? "translateY(4px)" : "translateY(0)",
          }}
        >
          {title}
        </h3>

        {/* Project count */}
        <p
          className="text-sm text-gray-500 dark:text-gray-400 transition-all duration-300"
          style={{
            opacity: isHovered ? 0.7 : 1,
          }}
        >
          {displayCount} {displayCount === 1 ? 'item' : 'items'}
        </p>

      </div>

      {/* Lightbox for viewing images */}
      <ImageLightbox
        projects={projects.slice(0, 3)}
        currentIndex={selectedIndex ?? 0}
        isOpen={selectedIndex !== null}
        onClose={handleCloseLightbox}
        sourceRect={sourceRect}
        onCloseComplete={handleCloseComplete}
        onNavigate={handleNavigate}
      />
    </>
  )
}

// Project Card Component
interface ProjectCardProps {
  image: string
  title: string
  type?: string
  delay: number
  isVisible: boolean
  index: number
  onClick: () => void
  isSelected: boolean
}

const ProjectCard = forwardRef<HTMLDivElement, ProjectCardProps>(
  ({ image, title, type, delay, isVisible, index, onClick, isSelected }, ref) => {
    const rotations = [-10, 0, 10]
    const translations = [-36, 0, 36]
    const [imgError, setImgError] = useState(false)

    // Fabric-style: clean cards with solid backgrounds
    const isNote = type === 'note'
    const hasValidImage = image && !imgError

    return (
      <div
        ref={ref}
        className={cn(
          "absolute overflow-hidden",
          "cursor-pointer hover:scale-105 transition-transform",
          isSelected && "opacity-0",
        )}
        style={{
          width: '56px',
          height: '72px',
          borderRadius: '8px',
          transform: isVisible
            ? `translateY(-60px) translateX(${translations[index]}px) rotate(${rotations[index]}deg) scale(1)`
            : "translateY(0px) translateX(0px) rotate(0deg) scale(0.5)",
          opacity: isSelected ? 0 : isVisible ? 1 : 0,
          transition: `all 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms`,
          zIndex: 10 - index,
          left: "-28px",
          top: "-36px",
          backgroundColor: hasValidImage ? '#fff' : getCardBackground(type),
          boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
      >
        {hasValidImage ? (
          /* Image content - with subtle rounded thumbnail */
          <div className="w-full h-full flex flex-col">
            <div className="flex-1 overflow-hidden rounded-t-[9px]">
              <img 
                src={image} 
                alt={title} 
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            </div>
            {/* Clean title bar at bottom */}
            <div className="px-1.5 py-1 bg-white border-t border-gray-100">
              <p className="text-[8px] font-medium text-gray-700 truncate leading-tight">
                {title?.replace('Note: ', '').slice(0, 18)}...
              </p>
            </div>
          </div>
        ) : isNote ? (
          /* Note card - Fabric style with icon and title */
          <div className="w-full h-full flex flex-col p-2">
            <div className="flex items-center gap-1 mb-1">
              <StickyNote className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <p className="text-[9px] font-medium text-gray-800 leading-tight line-clamp-3">
              {title?.replace('Note: ', '').replace('Notes on: ', '')}
            </p>
          </div>
        ) : (
          /* Other content types - icon centered with title below */
          <div className="w-full h-full flex flex-col items-center justify-center p-2">
            <div className="mb-1.5 p-1.5 rounded-md bg-white/60">
              {getTypeIcon(type)}
            </div>
            <p className="text-[8px] font-medium text-gray-600 text-center truncate w-full px-1">
              {title?.slice(0, 16)}...
            </p>
          </div>
        )}
      </div>
    )
  },
)

ProjectCard.displayName = "ProjectCard"

// Image Lightbox Component
interface ImageLightboxProps {
  projects: Project[]
  currentIndex: number
  isOpen: boolean
  onClose: () => void
  sourceRect: DOMRect | null
  onCloseComplete?: () => void
  onNavigate: (index: number) => void
}

function ImageLightbox({
  projects,
  currentIndex,
  isOpen,
  onClose,
  sourceRect,
  onCloseComplete,
  onNavigate,
}: ImageLightboxProps) {
  const [animationPhase, setAnimationPhase] = useState<"initial" | "animating" | "complete">("initial")
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  const [internalIndex, setInternalIndex] = useState(currentIndex)
  const [prevIndex, setPrevIndex] = useState(currentIndex)
  const [isSliding, setIsSliding] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const totalProjects = projects.length
  const hasNext = internalIndex < totalProjects - 1
  const hasPrev = internalIndex > 0

  const currentProject = projects[internalIndex]

  useEffect(() => {
    if (isOpen && currentIndex !== internalIndex && !isSliding) {
      setPrevIndex(internalIndex)
      setIsSliding(true)

      const timer = setTimeout(() => {
        setInternalIndex(currentIndex)
        setIsSliding(false)
      }, 400)

      return () => clearTimeout(timer)
    }
  }, [currentIndex, isOpen, internalIndex, isSliding])

  useEffect(() => {
    if (isOpen) {
      setInternalIndex(currentIndex)
      setPrevIndex(currentIndex)
      setIsSliding(false)
    }
  }, [isOpen, currentIndex])

  const navigateNext = useCallback(() => {
    if (internalIndex >= totalProjects - 1 || isSliding) return
    onNavigate(internalIndex + 1)
  }, [internalIndex, totalProjects, isSliding, onNavigate])

  const navigatePrev = useCallback(() => {
    if (internalIndex <= 0 || isSliding) return
    onNavigate(internalIndex - 1)
  }, [internalIndex, isSliding, onNavigate])

  const handleClose = useCallback(() => {
    setIsClosing(true)
    onClose()
    setTimeout(() => {
      setIsClosing(false)
      setShouldRender(false)
      setAnimationPhase("initial")
      onCloseComplete?.()
    }, 400)
  }, [onClose, onCloseComplete])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      if (e.key === "Escape") handleClose()
      if (e.key === "ArrowRight") navigateNext()
      if (e.key === "ArrowLeft") navigatePrev()
    }

    window.addEventListener("keydown", handleKeyDown)
    if (isOpen) {
      document.body.style.overflow = "hidden"
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      document.body.style.overflow = ""
    }
  }, [isOpen, handleClose, navigateNext, navigatePrev])

  useLayoutEffect(() => {
    if (isOpen && sourceRect) {
      setShouldRender(true)
      setAnimationPhase("initial")
      setIsClosing(false)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationPhase("animating")
        })
      })
      const timer = setTimeout(() => {
        setAnimationPhase("complete")
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isOpen, sourceRect])

  const handleDotClick = (idx: number) => {
    if (isSliding || idx === internalIndex) return
    onNavigate(idx)
  }

  if (!shouldRender || !currentProject) return null

  const getInitialStyles = (): React.CSSProperties => {
    if (!sourceRect) return {}

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const targetWidth = Math.min(768, viewportWidth - 64)
    const targetHeight = Math.min(viewportHeight * 0.85, 600)

    const targetX = (viewportWidth - targetWidth) / 2
    const targetY = (viewportHeight - targetHeight) / 2

    const scaleX = sourceRect.width / targetWidth
    const scaleY = sourceRect.height / targetHeight
    const scale = Math.max(scaleX, scaleY)

    const translateX = sourceRect.left + sourceRect.width / 2 - (targetX + targetWidth / 2)
    const translateY = sourceRect.top + sourceRect.height / 2 - (targetY + targetHeight / 2)

    return {
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      opacity: 1,
    }
  }

  const getFinalStyles = (): React.CSSProperties => {
    return {
      transform: "translate(0, 0) scale(1)",
      opacity: 1,
    }
  }

  const currentStyles = animationPhase === "initial" && !isClosing ? getInitialStyles() : getFinalStyles()

  return (
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8")}
      onClick={handleClose}
      style={{
        opacity: isClosing ? 0 : 1,
        transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-xl"
        style={{
          opacity: animationPhase === "initial" && !isClosing ? 0 : 1,
          transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleClose()
        }}
        className={cn(
          "absolute top-5 right-5 z-50",
          "w-10 h-10 flex items-center justify-center",
          "rounded-full bg-white/10 backdrop-blur-md",
          "border border-white/20",
          "text-white/70 hover:text-white hover:bg-white/20",
          "transition-all duration-300 ease-out hover:scale-105 active:scale-95",
        )}
        style={{
          opacity: animationPhase === "complete" && !isClosing ? 1 : 0,
          transform: animationPhase === "complete" && !isClosing ? "translateY(0)" : "translateY(-10px)",
          transition: "opacity 300ms ease-out, transform 300ms ease-out",
        }}
      >
        <X className="w-4 h-4" strokeWidth={2.5} />
      </button>

      {/* Prev button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          navigatePrev()
        }}
        disabled={!hasPrev || isSliding}
        className={cn(
          "absolute left-4 md:left-8 z-50",
          "w-12 h-12 flex items-center justify-center",
          "rounded-full bg-white/10 backdrop-blur-md",
          "border border-white/20",
          "text-white/70 hover:text-white hover:bg-white/20",
          "transition-all duration-300 ease-out hover:scale-110 active:scale-95",
          "disabled:opacity-0 disabled:pointer-events-none",
        )}
        style={{
          opacity: animationPhase === "complete" && !isClosing && hasPrev ? 1 : 0,
          transform: animationPhase === "complete" && !isClosing ? "translateX(0)" : "translateX(-20px)",
          transition: "opacity 300ms ease-out 150ms, transform 300ms ease-out 150ms",
        }}
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
      </button>

      {/* Next button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          navigateNext()
        }}
        disabled={!hasNext || isSliding}
        className={cn(
          "absolute right-4 md:right-8 z-50",
          "w-12 h-12 flex items-center justify-center",
          "rounded-full bg-white/10 backdrop-blur-md",
          "border border-white/20",
          "text-white/70 hover:text-white hover:bg-white/20",
          "transition-all duration-300 ease-out hover:scale-110 active:scale-95",
          "disabled:opacity-0 disabled:pointer-events-none",
        )}
        style={{
          opacity: animationPhase === "complete" && !isClosing && hasNext ? 1 : 0,
          transform: animationPhase === "complete" && !isClosing ? "translateX(0)" : "translateX(20px)",
          transition: "opacity 300ms ease-out 150ms, transform 300ms ease-out 150ms",
        }}
      >
        <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
      </button>

      <div
        ref={containerRef}
        className="relative z-10 w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...currentStyles,
          transform: isClosing ? "translate(0, 0) scale(0.95)" : currentStyles.transform,
          transition:
            animationPhase === "initial" && !isClosing
              ? "none"
              : "transform 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 400ms ease-out",
          transformOrigin: "center center",
        }}
      >
        <div
          className={cn(
            "relative overflow-hidden",
            "rounded-2xl",
            "bg-gray-900",
            "ring-1 ring-white/10",
            "shadow-2xl",
          )}
          style={{
            borderRadius: animationPhase === "initial" && !isClosing ? "8px" : "16px",
            transition: "border-radius 500ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-400 ease-out"
              style={{
                transform: `translateX(-${internalIndex * 100}%)`,
                transition: isSliding ? "transform 400ms cubic-bezier(0.32, 0.72, 0, 1)" : "none",
              }}
            >
              {projects.map((project, idx) => (
                <div key={project.id} className="w-full flex-shrink-0" style={{ minWidth: "100%" }}>
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-auto max-h-[70vh] object-contain bg-gray-950"
                    />
                  ) : (
                    <div 
                      className="w-full h-64 flex items-center justify-center"
                      style={{ background: getCardBackground(project.type) }}
                    >
                      <div className="text-center">
                        <div className="mb-3 p-3 rounded-xl bg-white/50 inline-block">
                          {getTypeIcon(project.type)}
                        </div>
                        <p className="text-sm font-medium text-gray-700">{project.title}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Subtle vignette effect */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-gray-900/20 via-transparent to-gray-900/10" />
          </div>

          <div
            className={cn("px-6 py-5", "bg-gray-900", "border-t border-white/10")}
            style={{
              opacity: animationPhase === "complete" && !isClosing ? 1 : 0,
              transform: animationPhase === "complete" && !isClosing ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 300ms ease-out 100ms, transform 300ms ease-out 100ms",
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-white tracking-tight truncate h-7">
                  {currentProject?.title}
                </h3>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-sm text-gray-400">
                    <kbd className="px-1.5 py-0.5 mx-0.5 text-xs font-medium bg-gray-800 text-gray-400 rounded border border-gray-700">
                      ←
                    </kbd>
                    <kbd className="px-1.5 py-0.5 mx-0.5 text-xs font-medium bg-gray-800 text-gray-400 rounded border border-gray-700">
                      →
                    </kbd>{" "}
                    to navigate
                  </p>
                  <div className="flex items-center gap-1.5">
                    {projects.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleDotClick(idx)}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all duration-300",
                          idx === internalIndex
                            ? "bg-white scale-110"
                            : "bg-gray-600 hover:bg-gray-500",
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
